/**
 * Landing v2 — premium illustrated dish set ("Organic Editorial" direction).
 *
 * Replaces the v1 MealArt mocks the owner rejected ("AI-generated plates").
 * Each dish is a deliberately composed top-down plate illustration in the
 * style high-end food brands use: ceramic plate with rim + inner well,
 * soft cast shadow, distinct hand-placed ingredient clusters with texture
 * (grain stippling, salmon striations, seed scatter, leaf veins), and a
 * consistent light direction (top-left). Honest by construction: this is
 * unmistakably illustration, never fake photography.
 *
 * Deterministic — no Math.random; scatter positions are hand-tuned tables
 * so SSR/tests are stable and every render is identical.
 */

import type { ReactElement } from "react"

type Dish = "buddha" | "salmon" | "oats" | "curry" | "salad" | "pasta"

const PLATE = {
  // Shared plate: rim ellipse, well, cast shadow. Light from top-left.
  shadow: <ellipse cx="102" cy="112" rx="86" ry="18" fill="#17352b" opacity="0.10" />,
  rim: (
    <>
      <circle cx="100" cy="100" r="88" fill="#fdfbf4" />
      <circle cx="100" cy="100" r="88" fill="none" stroke="#e5e1d2" strokeWidth="1.5" />
      <circle cx="100" cy="100" r="72" fill="#f7f3e9" />
      <path
        d="M32 78 A72 72 0 0 1 78 34"
        fill="none"
        stroke="#ffffff"
        strokeWidth="6"
        strokeLinecap="round"
        opacity="0.8"
      />
    </>
  ),
}

/** Stippled grain bed (rice/quinoa/bulgur) with hand-tuned scatter. */
function GrainBed({
  cx,
  cy,
  r,
  tone = "#f2e8c9",
  fleck = "#d9c98f",
}: {
  cx: number
  cy: number
  r: number
  tone?: string
  fleck?: string
}) {
  const dots = [
    [-14, -8],
    [-4, -16],
    [8, -12],
    [16, -2],
    [12, 10],
    [2, 16],
    [-10, 12],
    [-18, 2],
    [-6, -2],
    [6, 4],
    [0, -8],
    [10, -20],
    [-16, -16],
    [20, 8],
    [-2, 8],
    [18, -12],
  ]
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={tone} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#00000012" strokeWidth="1" />
      {dots.map(([dx, dy], i) => (
        <ellipse
          key={i}
          cx={cx + (dx * r) / 24}
          cy={cy + (dy * r) / 24}
          rx="2.2"
          ry="1.3"
          fill={fleck}
          transform={`rotate(${(i * 37) % 180} ${cx + (dx * r) / 24} ${cy + (dy * r) / 24})`}
        />
      ))}
    </g>
  )
}

/** Leafy cluster with vein detail. */
function Greens({
  cx,
  cy,
  scale = 1,
  tone = "#3e7d5a",
  dark = "#2d6a4f",
}: {
  cx: number
  cy: number
  scale?: number
  tone?: string
  dark?: string
}) {
  const leaves = [
    { r: 0, d: "M0 0 C -6 -14, 4 -22, 8 -10 C 11 -2, 5 2, 0 0" },
    { r: 70, d: "M0 0 C -6 -14, 4 -22, 8 -10 C 11 -2, 5 2, 0 0" },
    { r: 150, d: "M0 0 C -6 -14, 4 -22, 8 -10 C 11 -2, 5 2, 0 0" },
    { r: 220, d: "M0 0 C -6 -14, 4 -22, 8 -10 C 11 -2, 5 2, 0 0" },
    { r: 290, d: "M0 0 C -6 -14, 4 -22, 8 -10 C 11 -2, 5 2, 0 0" },
  ]
  return (
    <g transform={`translate(${cx} ${cy}) scale(${scale})`}>
      {leaves.map((l, i) => (
        <g key={i} transform={`rotate(${l.r})`}>
          <path d={l.d} fill={i % 2 ? tone : dark} />
          <path d="M1 -1 C 3 -6, 5 -12, 7 -14" stroke="#ffffff55" strokeWidth="0.8" fill="none" />
        </g>
      ))}
    </g>
  )
}

/** Halved cherry tomatoes with seeds. */
function Tomato({ cx, cy, r = 8 }: { cx: number; cy: number; r?: number }) {
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill="#e0563f" />
      <circle cx={cx} cy={cy} r={r * 0.72} fill="#ed8b70" />
      <circle cx={cx} cy={cy} r={r * 0.4} fill="#f6b7a3" />
      {[0, 60, 120, 180, 240, 300].map(a => (
        <ellipse
          key={a}
          cx={cx + Math.cos((a * Math.PI) / 180) * r * 0.52}
          cy={cy + Math.sin((a * Math.PI) / 180) * r * 0.52}
          rx="1.4"
          ry="0.9"
          fill="#c94a35"
          transform={`rotate(${a} ${cx + Math.cos((a * Math.PI) / 180) * r * 0.52} ${cy + Math.sin((a * Math.PI) / 180) * r * 0.52})`}
        />
      ))}
      <circle cx={cx - r * 0.3} cy={cy - r * 0.35} r={r * 0.16} fill="#ffffff" opacity="0.55" />
    </g>
  )
}

/** Avocado fan — overlapping crescent slices. */
function AvocadoFan({ cx, cy, rot = 0 }: { cx: number; cy: number; rot?: number }) {
  return (
    <g transform={`translate(${cx} ${cy}) rotate(${rot})`}>
      {[0, 1, 2, 3].map(i => (
        <path
          key={i}
          d={`M ${i * 7 - 10} 10 A 16 16 0 0 1 ${i * 7 - 2} -12 A 12 12 0 0 0 ${i * 7 - 8} 10 Z`}
          fill={i % 2 ? "#b7d989" : "#a3cc72"}
          stroke="#7fae55"
          strokeWidth="0.8"
        />
      ))}
      <path
        d="M -12 11 A 18 18 0 0 1 4 -14"
        fill="none"
        stroke="#5c8a3c"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </g>
  )
}

/** Salmon fillet with butter striations. */
function Salmon({ cx, cy, rot = -18 }: { cx: number; cy: number; rot?: number }) {
  return (
    <g transform={`translate(${cx} ${cy}) rotate(${rot})`}>
      <path
        d="M-26 -12 Q 0 -22 26 -10 Q 32 0 24 12 Q 0 20 -22 12 Q -32 0 -26 -12 Z"
        fill="#ef8a63"
      />
      <path d="M-26 -12 Q 0 -22 26 -10 Q 28 -6 28 -2 Q 0 -12 -27 -6 Z" fill="#f6a67f" />
      {[-16, -6, 4, 14].map(x => (
        <path
          key={x}
          d={`M ${x} -12 Q ${x + 4} 0 ${x - 2} 13`}
          stroke="#fbe0d2"
          strokeWidth="2.4"
          fill="none"
          strokeLinecap="round"
          opacity="0.85"
        />
      ))}
      <ellipse cx="-14" cy="-8" rx="6" ry="2.5" fill="#ffffff" opacity="0.35" />
    </g>
  )
}

/** Soft-boiled egg half. */
function EggHalf({ cx, cy, rot = 0 }: { cx: number; cy: number; rot?: number }) {
  return (
    <g transform={`translate(${cx} ${cy}) rotate(${rot})`}>
      <ellipse cx="0" cy="0" rx="11" ry="14" fill="#fffdf6" stroke="#eee5cd" strokeWidth="1" />
      <circle cx="0" cy="-1" r="6" fill="#e8b758" />
      <circle cx="-1.5" cy="-2.5" r="2" fill="#f3cf8b" />
    </g>
  )
}

/** Sesame + herb scatter for finishing. */
function Garnish({ cx, cy, spread = 26 }: { cx: number; cy: number; spread?: number }) {
  const bits = [
    [-0.8, -0.4, 0],
    [0.5, -0.7, 40],
    [0.9, 0.3, 80],
    [-0.3, 0.8, 120],
    [0.1, -0.1, 160],
    [-0.9, 0.5, 200],
  ]
  return (
    <g>
      {bits.map(([x, y, r], i) => (
        <ellipse
          key={i}
          cx={cx + x * spread}
          cy={cy + y * spread}
          rx="1.8"
          ry="0.9"
          fill={i % 2 ? "#f7f3e9" : "#2d6a4f"}
          transform={`rotate(${r} ${cx + x * spread} ${cy + y * spread})`}
        />
      ))}
    </g>
  )
}

const DISHES: Record<Dish, ReactElement> = {
  buddha: (
    <g>
      <GrainBed cx={100} cy={104} r={52} />
      <Greens cx={72} cy={82} scale={1.5} />
      <AvocadoFan cx={128} cy={84} rot={20} />
      <Tomato cx={126} cy={122} r={9} />
      <Tomato cx={108} cy={132} r={7} />
      <EggHalf cx={78} cy={120} rot={-12} />
      <Garnish cx={100} cy={102} spread={30} />
    </g>
  ),
  salmon: (
    <g>
      <GrainBed cx={100} cy={106} r={50} tone="#eef3df" fleck="#c9d9a8" />
      <Salmon cx={102} cy={92} />
      <Greens cx={66} cy={118} scale={1.3} />
      <Tomato cx={136} cy={116} r={8} />
      <Garnish cx={104} cy={112} spread={26} />
    </g>
  ),
  oats: (
    <g>
      <circle cx={100} cy={100} r={54} fill="#f6ead3" />
      <circle cx={100} cy={100} r={54} fill="none" stroke="#00000010" strokeWidth="1" />
      <path d="M 60 96 Q 100 82 140 98 Q 132 120 100 122 Q 68 120 60 96 Z" fill="#fbf3e2" />
      {[
        [86, 92, 6],
        [104, 88, 7],
        [96, 102, 5],
      ].map(([x, y, r], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r={r} fill="#7c4a6d" />
          <circle cx={x! - 1.5} cy={y! - 1.5} r={r! * 0.3} fill="#ffffff" opacity="0.4" />
        </g>
      ))}
      <AvocadoFan cx={124} cy={108} rot={-160} />
      <path
        d="M 70 110 q 8 8 20 6"
        stroke="#e8b758"
        strokeWidth="3.4"
        fill="none"
        strokeLinecap="round"
      />
      <Garnish cx={100} cy={100} spread={32} />
    </g>
  ),
  curry: (
    <g>
      <circle cx={100} cy={100} r={54} fill="#f3d9a8" />
      <circle cx={100} cy={100} r={46} fill="#e8b758" />
      <path d="M 62 92 Q 100 76 138 94 Q 128 118 100 120 Q 72 118 62 92 Z" fill="#efc46f" />
      {[
        [84, 94],
        [110, 90],
        [98, 108],
        [120, 104],
      ].map(([x, y], i) => (
        <rect
          key={i}
          x={x! - 6}
          y={y! - 6}
          width="12"
          height="12"
          rx="3.5"
          fill={i % 2 ? "#fdfbf4" : "#f6e7c8"}
          transform={`rotate(${i * 24} ${x} ${y})`}
        />
      ))}
      <GrainBed cx={64} cy={124} r={22} />
      <Greens cx={136} cy={124} scale={1.1} />
      <Garnish cx={100} cy={98} spread={28} />
    </g>
  ),
  salad: (
    <g>
      <Greens cx={92} cy={92} scale={2.4} />
      <Greens cx={118} cy={112} scale={1.8} tone="#5c9a72" dark="#3e7d5a" />
      <Tomato cx={76} cy={118} r={9} />
      <Tomato cx={130} cy={84} r={8} />
      <EggHalf cx={112} cy={70} rot={16} />
      <AvocadoFan cx={70} cy={84} rot={-30} />
      <Garnish cx={100} cy={100} spread={34} />
    </g>
  ),
  pasta: (
    <g>
      {[0, 1, 2, 3, 4].map(i => (
        <path
          key={i}
          d={`M ${58 + i * 8} ${120 - i * 4} Q 100 ${54 + i * 10} ${142 - i * 8} ${118 - i * 3}`}
          stroke={i % 2 ? "#f2d98c" : "#e8c76a"}
          strokeWidth="7"
          fill="none"
          strokeLinecap="round"
        />
      ))}
      <Tomato cx={94} cy={96} r={9} />
      <Tomato cx={116} cy={106} r={7} />
      <Greens cx={124} cy={82} scale={1.2} />
      <Garnish cx={100} cy={104} spread={30} />
    </g>
  ),
}

export type { Dish }


export function DishArt({
  dish,
  className,
  size = 96,
}: {
  dish: Dish
  className?: string
  size?: number
}) {
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-hidden="true"
      focusable="false"
    >
      {PLATE.shadow}
      {PLATE.rim}
      {DISHES[dish]}
    </svg>
  )
}
